import { useCallback, useEffect, useMemo, useState } from "react";
import {
    createAndAssignVacancyTestSetting,
    getTests,
    getVacancyTestSettings,
    getVacancyTestSettingsLibrary,
    updateAndAssignVacancyTestSetting,
} from "../../services/testApi";
import { useAlertContext } from "../../context/AlertContext";

export const CANDIDATE_PLACEHOLDER = "[Имя кандидата]";
export const VACANCY_PLACEHOLDER = "[Название вакансии]";
export const TEST_PLACEHOLDER = "[Название теста]";
export const TEST_URL_PLACEHOLDER = "[Ссылка на тест]";
export const DEFAULT_TEST_MESSAGE =
    `Здравствуйте, ${CANDIDATE_PLACEHOLDER}!\n\n` +
    "Спасибо за отклик на вакансию «{vacancy_name}». Пожалуйста, пройдите тест «{test_name}»:\n{test_url}";

const MESSAGE_PLACEHOLDERS = [
    ["{candidate_name}", CANDIDATE_PLACEHOLDER],
    ["{vacancy_name}", VACANCY_PLACEHOLDER],
    ["{test_name}", TEST_PLACEHOLDER],
    ["{test_url}", TEST_URL_PLACEHOLDER],
];

export const toEditorMessage = (value) => MESSAGE_PLACEHOLDERS.reduce(
    (message, [apiPlaceholder, editorPlaceholder]) => message.replaceAll(apiPlaceholder, editorPlaceholder),
    String(value || ""),
);

export const toApiMessage = (value) => MESSAGE_PLACEHOLDERS.reduce(
    (message, [apiPlaceholder, editorPlaceholder]) => message.replaceAll(editorPlaceholder, apiPlaceholder),
    String(value || ""),
);

export const DELIVERY_FILTER_OPTIONS = [
    ["all", "Всем подходящим кандидатам"],
    ["interview", "Только на этапе собеседования"],
    ["not_tested", "Только без ранее назначенных тестов"],
];

function normalizeItem(item, test) {
    return {
        id: item?.id ?? null,
        test_id: Number(item?.test_id ?? test?.id),
        test_name: item?.test_name || test?.name || `Тест #${item?.test_id ?? test?.id}`,
        passing_score_percent: Number(item?.passing_score_percent ?? test?.passing_score_percent ?? 80),
        priority: Math.max(0, Number(item?.priority) || 0),
        invitation_message: toEditorMessage(item?.invitation_message || DEFAULT_TEST_MESSAGE),
        failure_message: toEditorMessage(item?.failure_message),
        delivery_filter: item?.delivery_filter || "all",
        reminder_policy: item?.reminder_policy || null,
    };
}

const REMINDER_MODES = [
    ["none", "Не напоминать"],
    ["interval", "Через N дней, несколько раз"],
    ["daily", "Каждый день до прохождения"],
];

/** Правка №1: напоминания о тесте и срок до «отказался: тест» для этапа рассылки. */
function ReminderPolicyFields({ item, onChange }) {
    const policy = item.reminder_policy || null;
    const set = (patch) => onChange({
        reminder_policy: {
            mode: "interval", first_after_days: 2, repeat_every_days: 3, max_reminders: 1, give_up_after_days: 7,
            ...(policy || {}), ...patch,
        },
    });
    const input = "mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100";
    const num = (value, min, max) => Math.max(min, Math.min(max, Number(value) || min));
    return (
        <fieldset className="mt-4 border-t border-slate-100 pt-4" data-testid={`campaign-reminders-${item.test_id}`}>
            <legend className="text-sm font-semibold text-slate-800">Напоминания о тесте</legend>
            {!policy ? (
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                    <span>Не настроено: кандидату не напоминаем, статус по сроку не меняем.</span>
                    <button type="button" onClick={() => set({})} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Настроить</button>
                </div>
            ) : (
                <>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                        <label className="block text-sm font-semibold text-slate-700 xl:col-span-2">Как напоминать
                            <select aria-label={`Напоминания теста «${item.test_name}»`} value={policy.mode} onChange={(event) => set({ mode: event.target.value })} className={input}>
                                {REMINDER_MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </label>
                        {policy.mode !== "none" ? (
                            <label className="block text-sm font-semibold text-slate-700">Первое через, дн.
                                <input type="number" min="1" max="30" value={policy.first_after_days} onChange={(event) => set({ first_after_days: num(event.target.value, 1, 30) })} className={input} />
                            </label>
                        ) : null}
                        {policy.mode === "interval" ? (
                            <>
                                <label className="block text-sm font-semibold text-slate-700">Повтор каждые, дн.
                                    <input type="number" min="1" max="30" value={policy.repeat_every_days} onChange={(event) => set({ repeat_every_days: num(event.target.value, 1, 30) })} className={input} />
                                </label>
                                <label className="block text-sm font-semibold text-slate-700">Всего напоминаний
                                    <input type="number" min="1" max="30" value={policy.max_reminders} onChange={(event) => set({ max_reminders: num(event.target.value, 1, 30) })} className={input} />
                                </label>
                            </>
                        ) : null}
                    </div>
                    <div className="mt-3 flex flex-wrap items-end gap-3">
                        <label className="block text-sm font-semibold text-slate-700">«Отказался: тест», если не прошёл за, дн.
                            <input type="number" min="0" max="90" placeholder="не менять" value={policy.give_up_after_days ?? ""}
                                onChange={(event) => set({ give_up_after_days: event.target.value === "" || Number(event.target.value) === 0 ? null : num(event.target.value, 1, 90) })}
                                className={input} />
                        </label>
                        <button type="button" onClick={() => onChange({ reminder_policy: null })} className="mb-1 text-xs font-semibold text-slate-500 hover:text-slate-900">Отключить напоминания</button>
                    </div>
                </>
            )}
        </fieldset>
    );
}

export function normalizeCampaign(settings, tests, vacancyName = "") {
    const byId = new Map((tests || []).map((test) => [Number(test.id), test]));
    const explicitItems = Array.isArray(settings?.items) ? settings.items : [];
    const legacyIds = Array.isArray(settings?.test_ids) ? settings.test_ids.map(Number) : [];
    const items = explicitItems.length
        ? explicitItems.map((item) => normalizeItem(item, byId.get(Number(item.test_id))))
        : legacyIds.map((testId) => normalizeItem({
            test_id: testId,
            priority: 0,
            invitation_message: settings?.invitation_message || DEFAULT_TEST_MESSAGE,
            delivery_filter: settings?.delivery_filters?.[testId] || "all",
        }, byId.get(testId)));
    return {
        id: settings?.setting_id ?? settings?.id ?? null,
        updated_at: settings?.setting_updated_at ?? settings?.updated_at ?? null,
        name: settings?.setting_name || settings?.name || `Рассылка: ${vacancyName || "новая вакансия"}`,
        description: settings?.description || "",
        vacancy_ids: settings?.vacancy_ids || [],
        items,
    };
}

export function validateCampaign(campaign) {
    if (!campaign.name.trim()) return "Укажите название схемы рассылки";
    if (campaign.items.some((item) =>
        !Number.isInteger(Number(item.priority))
        || Number(item.priority) < 0
        || Number(item.priority) > 10000
    )) {
        return "Порядок отправки должен быть целым положительным числом";
    }
    const priorities = campaign.items.map((item) => item.priority).filter((priority) => priority > 0);
    if (new Set(priorities).size !== priorities.length) {
        return "Порядки отправки тестов не должны повторяться";
    }
    if (priorities.length) {
        const firstPriority = Math.min(...priorities);
        if (campaign.items.some((item) =>
            item.priority > firstPriority && item.delivery_filter === "not_tested"
        )) {
            return "Фильтр «без ранее назначенных тестов» можно использовать только для первого активного этапа";
        }
    }
    if (campaign.items.some((item) => !item.invitation_message.trim())) {
        return "Для каждого выбранного теста заполните сообщение кандидату";
    }
    return null;
}

function campaignPayload(campaign) {
    const payload = {
        name: campaign.name.trim(),
        description: campaign.description.trim() || null,
        items: campaign.items.map((item) => ({
            test_id: Number(item.test_id),
            priority: Number(item.priority) || 0,
            invitation_message: toApiMessage(item.invitation_message).trim(),
            failure_message: toApiMessage(item.failure_message).trim() || null,
            delivery_filter: item.delivery_filter,
            reminder_policy: item.reminder_policy || null,
        })),
    };
    if (campaign.id && campaign.updated_at) {
        payload.expected_updated_at = campaign.updated_at;
    }
    return payload;
}

export default function VacancyTestCampaignEditor({ vacancyId, vacancyName, onClose }) {
    const { showAlert } = useAlertContext();
    const [tests, setTests] = useState([]);
    const [library, setLibrary] = useState([]);
    const [campaign, setCampaign] = useState(() => normalizeCampaign(null, [], vacancyName));
    const [initialSnapshot, setInitialSnapshot] = useState("");
    const [assignedSettingId, setAssignedSettingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [expandedTestId, setExpandedTestId] = useState(null);

    const applyCampaign = useCallback((next) => {
        setCampaign(next);
        setExpandedTestId(next.items[0]?.test_id ?? null);
        setInitialSnapshot(JSON.stringify(next));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [testRows, current, saved] = await Promise.all([
                getTests(),
                getVacancyTestSettings(vacancyId),
                getVacancyTestSettingsLibrary(),
            ]);
            const normalizedTests = Array.isArray(testRows) ? testRows : testRows?.items || [];
            const normalizedLibrary = (Array.isArray(saved) ? saved : []).map((item) =>
                normalizeCampaign(item, normalizedTests, vacancyName)
            );
            setTests(normalizedTests);
            setLibrary(normalizedLibrary);
            setAssignedSettingId(current?.setting_id ?? null);
            applyCampaign(normalizeCampaign(current, normalizedTests, vacancyName));
        } catch (error) {
            showAlert(`Не удалось загрузить схему рассылки: ${error?.message || error}`, "error");
        } finally {
            setLoading(false);
        }
    }, [applyCampaign, showAlert, vacancyId, vacancyName]);

    useEffect(() => { load(); }, [load]);

    const selectedIds = useMemo(
        () => new Set(campaign.items.map((item) => Number(item.test_id))),
        [campaign.items]
    );
    const filteredTests = useMemo(() => {
        const query = search.trim().toLowerCase();
        return tests.filter((test) => !query || String(test.name || "").toLowerCase().includes(query));
    }, [search, tests]);
    const orderedItems = useMemo(() => [...campaign.items].sort((a, b) => {
        if (a.priority === 0 && b.priority !== 0) return 1;
        if (b.priority === 0 && a.priority !== 0) return -1;
        return a.priority - b.priority || a.test_id - b.test_id;
    }), [campaign.items]);
    const dirty = JSON.stringify(campaign) !== initialSnapshot
        || Number(campaign.id || 0) !== Number(assignedSettingId || 0);
    const currentLibraryEntry = library.find((item) => Number(item.id) === Number(campaign.id));
    const linkedVacancyIds = currentLibraryEntry?.vacancy_ids || campaign.vacancy_ids || [];
    const otherVacancyCount = linkedVacancyIds.filter((id) => Number(id) !== Number(vacancyId)).length;
    const duplicatePriorities = new Set(
        campaign.items
            .filter((item, index, rows) => item.priority > 0 && rows.some(
                (other, otherIndex) => otherIndex !== index && other.priority === item.priority
            ))
            .map((item) => item.priority)
    );

    const updateItem = (testId, patch) => setCampaign((previous) => ({
        ...previous,
        items: previous.items.map((item) =>
            Number(item.test_id) === Number(testId) ? { ...item, ...patch } : item
        ),
    }));

    const includeInOrder = (testId) => setCampaign((previous) => {
        const usedPriorities = new Set(
            previous.items.map((item) => Number(item.priority)).filter((priority) => priority > 0)
        );
        let nextPriority = 1;
        while (usedPriorities.has(nextPriority)) nextPriority += 1;
        return {
            ...previous,
            items: previous.items.map((item) =>
                Number(item.test_id) === Number(testId)
                    ? { ...item, priority: nextPriority }
                    : item
            ),
        };
    });

    const excludeFromOrder = (testId) => updateItem(testId, { priority: 0 });

    const toggleTest = (test) => {
        const testId = Number(test.id);
        const wasSelected = selectedIds.has(testId);
        setExpandedTestId((current) => wasSelected ? (current === testId ? null : current) : testId);
        setCampaign((previous) => wasSelected
            ? { ...previous, items: previous.items.filter((item) => Number(item.test_id) !== testId) }
            : { ...previous, items: [...previous.items, normalizeItem(null, test)] }
        );
    };

    const chooseSavedCampaign = (value) => {
        if (!value) {
            applyCampaign(normalizeCampaign(null, tests, vacancyName));
            return;
        }
        const selected = library.find((item) => String(item.id) === String(value));
        if (selected) applyCampaign({ ...selected, items: selected.items.map((item) => ({ ...item })) });
    };

    const makeCopy = () => {
        setCampaign((previous) => ({
            ...previous,
            id: null,
            updated_at: null,
            name: `${previous.name || "Схема рассылки"} — копия`,
            vacancy_ids: [],
            items: previous.items.map(({ id, ...item }) => ({ ...item, id: null })),
        }));
        showAlert("Создана локальная копия. Сохраните её, чтобы применить к вакансии.", "info");
    };

    const save = async () => {
        const validationError = validateCampaign(campaign);
        if (validationError) {
            showAlert(validationError, "error");
            return;
        }
        setSaving(true);
        try {
            if (campaign.id) {
                await updateAndAssignVacancyTestSetting(
                    vacancyId, campaign.id, campaignPayload(campaign)
                );
            } else {
                await createAndAssignVacancyTestSetting(
                    vacancyId, campaignPayload(campaign)
                );
            }
            showAlert("Схема тестирования сохранена и применена к вакансии", "success");
            await load();
        } catch (error) {
            showAlert(`Не удалось сохранить схему: ${error?.message || error}`, "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-sm font-medium text-slate-600 shadow-sm">Загрузка настройки рассылки…</div>;
    }

    return (
        <div className="space-y-4" data-testid="vacancy-test-campaign-editor">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-slate-950">Схема рассылки</h2>
                    <p className="mt-0.5 text-sm text-slate-600">Сохранённую схему можно использовать для нескольких вакансий.</p>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] xl:items-end">
                        <label className="text-sm font-semibold text-slate-700">
                            Сохранённая схема
                            <select
                                aria-label="Сохранённая схема рассылки"
                                value={campaign.id || ""}
                                onChange={(event) => chooseSavedCampaign(event.target.value)}
                                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                            >
                                <option value="">Новая схема</option>
                                {library.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm font-semibold text-slate-700">
                            Название схемы
                            <input
                                aria-label="Название схемы рассылки"
                                value={campaign.name}
                                maxLength={200}
                                onChange={(event) => setCampaign((value) => ({ ...value, name: event.target.value }))}
                                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm font-normal text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                            />
                        </label>
                    <button type="button" onClick={makeCopy} disabled={!campaign.items.length}
                        className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50">
                        Создать копию
                    </button>
                </div>
                {otherVacancyCount > 0 ? (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-5 text-amber-950" role="status">
                        Эта схема используется ещё в {otherVacancyCount} {otherVacancyCount === 1 ? "вакансии" : "вакансиях"}. Изменения этапов применятся ко всем ним. Для независимой настройки создайте копию.
                    </div>
                ) : null}
                {Number(campaign.id || 0) !== Number(assignedSettingId || 0) ? (
                    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm leading-5 text-sky-950" role="status">
                        Вы выбрали другую схему. Она будет назначена вакансии только после нажатия «Сохранить и применить».
                    </div>
                ) : null}
            </section>

            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
                <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-4">
                    <div className="flex items-center justify-between gap-3">
                        <div><h2 className="text-base font-bold text-slate-950">Доступные тесты</h2><p className="text-xs text-slate-500">Нажмите, чтобы добавить или убрать</p></div>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">{campaign.items.length}</span>
                    </div>
                    <input aria-label="Поиск теста" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Найти тест…"
                        className="mt-3 h-9 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100" />
                    <div className="mt-3 max-h-[min(56vh,520px)] space-y-1.5 overflow-y-auto pr-1">
                        {filteredTests.map((test) => {
                            const checked = selectedIds.has(Number(test.id));
                            return <button key={test.id} type="button" onClick={() => toggleTest(test)}
                                aria-pressed={checked}
                                data-testid={`campaign-test-toggle-${test.id}`}
                                className={`w-full rounded-lg border px-3 py-2.5 text-left transition ${checked ? "border-amber-300 bg-amber-50" : "border-transparent bg-slate-50 hover:border-slate-200 hover:bg-white"}`}>
                                <span className="flex items-center gap-2.5"><span className={`grid h-5 w-5 shrink-0 place-items-center rounded border text-xs font-bold ${checked ? "border-amber-500 bg-amber-500 text-white" : "border-slate-300 bg-white text-slate-500"}`}>{checked ? "✓" : "+"}</span>
                                <span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-900" title={test.name}>{test.name}</span><span className="block text-xs text-slate-500">Проходной порог {test.passing_score_percent || 80}%</span></span></span>
                            </button>;
                        })}
                        {filteredTests.length === 0 ? <p className="px-2 py-4 text-center text-sm text-slate-500">Ничего не найдено</p> : null}
                    </div>
                </aside>

                <main className="min-w-0 space-y-2.5">
                    <div className="flex flex-wrap items-end justify-between gap-2 px-1 pb-1">
                        <div><h2 className="text-lg font-bold text-slate-950">Порядок отправки</h2><p className="mt-0.5 text-sm text-slate-600">Следующий тест назначится после успешного прохождения предыдущего.</p></div>
                        <span className="text-xs font-semibold text-slate-500">{campaign.items.filter((item) => item.priority > 0).length} активных · {campaign.items.length} всего</span>
                    </div>
                    {orderedItems.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center"><p className="text-base font-semibold text-slate-800">Добавьте тесты из списка слева</p><p className="mt-1 text-sm text-slate-600">Здесь появится порядок отправки и настройки сообщений.</p></div>
                    ) : orderedItems.map((item) => (
                        <article key={item.test_id} data-testid={`campaign-item-${item.test_id}`} className={`overflow-hidden rounded-xl border bg-white shadow-sm ${duplicatePriorities.has(item.priority) ? "border-rose-300" : "border-slate-200"}`}>
                            <button type="button" aria-expanded={expandedTestId === item.test_id} aria-controls={expandedTestId === item.test_id ? `campaign-item-settings-${item.test_id}` : undefined}
                                aria-label={`Настроить тест «${item.test_name}»`}
                                onClick={() => setExpandedTestId((current) => current === item.test_id ? null : item.test_id)}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50">
                                {item.priority > 0 ? <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-amber-100 text-sm font-bold text-amber-900" aria-label={`Этап ${item.priority}`}>{item.priority}</span>
                                    : <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-sm font-bold text-slate-500" aria-hidden="true">—</span>}
                                <span className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5"><span className="truncate text-sm font-bold text-slate-950 sm:text-base">{item.test_name}</span>{item.priority === 0 ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">Отправляется последним</span> : null}</span>
                                    <span className="mt-0.5 block text-xs text-slate-500">Проходной результат: не менее {item.passing_score_percent}% · {DELIVERY_FILTER_OPTIONS.find(([value]) => value === item.delivery_filter)?.[1] || "Всем подходящим кандидатам"}</span>
                                </span>
                                <span className="shrink-0 text-xs font-semibold text-slate-600">{expandedTestId === item.test_id ? "Свернуть" : "Настроить"} <span aria-hidden="true">{expandedTestId === item.test_id ? "⌃" : "⌄"}</span></span>
                            </button>
                            {duplicatePriorities.has(item.priority) ? <p className="mx-4 mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">Этот порядок уже указан у другого теста.</p> : null}
                            {expandedTestId === item.test_id ? <div id={`campaign-item-settings-${item.test_id}`} className="border-t border-slate-100 px-4 pb-4 pt-4 sm:px-5">
                            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_210px] sm:items-start">
                                <label className="block text-sm font-semibold text-slate-700">Кому назначать этот этап
                                    <select aria-label={`Аудитория теста «${item.test_name}»`} value={item.delivery_filter}
                                        onChange={(event) => updateItem(item.test_id, { delivery_filter: event.target.value })}
                                        className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100">
                                        {DELIVERY_FILTER_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </label>
                                {item.priority === 0 ? (
                                    <div className="rounded-lg bg-slate-50 p-3">
                                        <span className="block text-sm font-semibold text-slate-900">Отправляется последним</span>
                                        <button type="button" onClick={() => includeInOrder(item.test_id)} className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-700">Добавить в порядок отправки</button>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700">
                                            Порядок отправки
                                            <input aria-label={`Порядок отправки теста «${item.test_name}»`} type="number" min="1" max="10000" value={item.priority}
                                                onChange={(event) => updateItem(item.test_id, { priority: Math.max(1, Number(event.target.value) || 1) })}
                                                className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100" />
                                        </label>
                                        <button type="button" onClick={() => excludeFromOrder(item.test_id)} className="mt-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900">Исключить из порядка</button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 grid gap-3 border-t border-slate-100 pt-4 xl:grid-cols-2">
                                <label className="block text-sm font-semibold text-slate-800">Приглашение с ссылкой на тест <span className="text-rose-600">*</span>
                                    <textarea aria-label={`Сообщение теста «${item.test_name}»`} rows={5} maxLength={4000} value={item.invitation_message}
                                        onChange={(event) => updateItem(item.test_id, { invitation_message: event.target.value })}
                                        className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal leading-5 text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100" />
                                </label>
                                <label className="block text-sm font-semibold text-slate-800">Если тест не пройден <span className="font-normal text-slate-500">· необязательно</span>
                                    <textarea aria-label={`Сообщение при непрохождении теста «${item.test_name}»`} rows={5} maxLength={4000} value={item.failure_message}
                                        onChange={(event) => updateItem(item.test_id, { failure_message: event.target.value })}
                                        placeholder={`Здравствуйте, ${CANDIDATE_PLACEHOLDER}. Спасибо, что прошли тест «${TEST_PLACEHOLDER}».`}
                                        className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal leading-5 text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100" />
                                </label>
                            </div>
                            <details className="mt-2 text-xs text-slate-600"><summary className="cursor-pointer font-semibold text-slate-600 hover:text-slate-900">Что подставится в сообщения автоматически?</summary><p className="mt-1.5 leading-5">{CANDIDATE_PLACEHOLDER}, {VACANCY_PLACEHOLDER}, {TEST_PLACEHOLDER} и {TEST_URL_PLACEHOLDER}. Если текст при непрохождении пуст, сообщение не отправляется.</p></details>
                            <ReminderPolicyFields item={item} onChange={(patch) => updateItem(item.test_id, patch)} />
                            {item.delivery_filter === "not_tested" && item.priority > Math.min(
                                ...campaign.items.filter((row) => row.priority > 0).map((row) => row.priority)
                            ) ? <p className="mt-2 text-sm font-semibold text-rose-700">Этот фильтр подходит только для первого активного этапа.</p> : null}
                            </div> : null}
                        </article>
                    ))}
                </main>
            </div>

            <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
                <p className="text-sm text-slate-600"><b className="text-slate-950">{campaign.items.filter((item) => item.priority > 0).length}</b> активных этапов{dirty ? <span className="ml-2 font-semibold text-amber-700">· есть несохранённые изменения</span> : null}</p>
                <div className="flex flex-wrap gap-2"><button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">К вакансиям</button><button type="button" onClick={save} disabled={saving || !dirty} data-testid="campaign-save" className="h-10 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">{saving ? "Сохранение…" : "Сохранить и применить"}</button></div>
            </div>
        </div>
    );
}
